import { useCallback } from "react";
import { useACLContext } from "../context";
import type {
  PermissionAction,
  ResourceType,
  User,
  PermissionRule,
  RoleName,
} from "@shield-acl/core";

/**
 * Hook principal do ACL que expõe todas as funcionalidades
 *
 * @returns Objeto com métodos e estado do ACL
 *
 * @example
 * ```tsx
 * const { can, cannot, evaluate, user, setUser } = useACL()
 *
 * if (can("posts.create")) {
 *   // Usuário pode criar posts
 * }
 *
 * const result = evaluate("posts.edit", "posts", { postId: 123 })
 * console.log(result.reason)
 * ```
 */
export function useACL<TContext = any>() {
  const { engine, user, setUser, can, evaluate } = useACLContext<TContext>();

  /**
   * Verifica se usuário NÃO tem permissão (inverso de can)
   */
  const cannot = useCallback(
    (
      action: PermissionAction,
      resource?: ResourceType,
      context?: TContext,
    ): boolean => {
      return !can(action, resource, context);
    },
    [can],
  );

  /**
   * Obtém todas as permissões do usuário atual
   */
  const permissions = useCallback((): PermissionRule<TContext>[] => {
    if (!user) return [];
    return engine.getUserPermissions(user);
  }, [engine, user]);

  /**
   * Obtém todas as roles do usuário atual
   */
  const roles = useCallback((): RoleName[] => {
    if (!user) return [];
    return user.roles;
  }, [user]);

  /**
   * Limpa o cache do engine
   */
  const clearCache = useCallback(() => {
    engine.clearCache();
  }, [engine]);

  return {
    // Estado
    user,
    setUser,

    // Verificações
    can,
    cannot,
    evaluate,

    // Dados
    permissions,
    roles,

    // Engine
    engine,

    // Utilidades
    clearCache,
  };
}

/**
 * Type helper para inferir o tipo de contexto do useACL
 */
export type UseACLReturn<TContext = any> = ReturnType<typeof useACL<TContext>>;
